/**
 * Dates shown in the dev-shell title bar.
 *
 * The dev-shell and the packages it renders are versioned independently: one deployed shell
 * serves many packages, each loaded by `?exportId=...`. So two dates are shown, each with the
 * full timestamps behind a tooltip:
 *
 * - `shellAt`   - newest `Last-Modified` across the shell's own document and bundles
 * - `packageAt` - `Last-Modified` of the package's `ambrosia.js`, i.e. when it was written to
 *                 the filesystem / S3. Falls back to the export's `completedAt` when the header
 *                 is not readable, which is the case for a cross-origin CloudFront package.
 *
 * Every lookup is best-effort: anything that cannot be resolved is omitted rather than
 * failing the others, so the title bar never blanks out or throws.
 */

export interface ShellPackageDates {
  shellAt?: string;
  shellTooltip?: string;
  packageAt?: string;
  packageTooltip?: string;
}

/** `document.lastModified` falls back to "now" when there is no header - only trust older values. */
const DOCUMENT_LAST_MODIFIED_MIN_AGE_MS = 5 * 60 * 1000;

/** `exportMetadata` sits at byte offset ~306 of `ambrosia.js`, so the head of the file is enough. */
const AMBROSIA_HEAD_BYTES = 8192;

/** The shell has a handful of scripts; probe a few rather than every chunk on the page. */
const MAX_SHELL_ASSET_PROBES = 5;

const cache = new Map<string, Promise<ShellPackageDates>>();

/** Formats to "Aug 6", adding the year only when it is not the current one. */
export function formatShortDate(value?: string | number | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (date.getFullYear() !== new Date().getFullYear()) {
    options.year = "numeric";
  }
  return date.toLocaleDateString("en-US", options);
}

async function readLastModified(url: string): Promise<string | undefined> {
  try {
    const res = await fetch(url, { method: "HEAD" });
    if (!res.ok) {
      return undefined;
    }
    return res.headers.get("last-modified") ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The shell's own assets: its document plus the scripts it loaded. Third-party scripts (the
 * Pearson session js) are skipped - they are not part of the shell and are not same-origin.
 */
function getShellAssetUrls(): string[] {
  const urls = [`${window.location.origin}${window.location.pathname}`];

  for (const script of Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    try {
      const url = new URL(script.src, window.location.href);
      if (url.origin === window.location.origin) {
        urls.push(`${url.origin}${url.pathname}`);
      }
    } catch {
      // Ignore an unparseable src.
    }
  }

  return Array.from(new Set(urls)).slice(0, MAX_SHELL_ASSET_PROBES);
}

/**
 * Newest `Last-Modified` across the shell's own assets. All same-origin, so the header is
 * readable. The bundle is included rather than just `index.html` because a rebuild that leaves
 * `index.html` byte-identical does not touch its mtime, which would report a stale shell date.
 */
export async function getShellLastModified(): Promise<string | undefined> {
  const found = await Promise.all(getShellAssetUrls().map(readLastModified));

  let newest: { value: string; time: number } | undefined;
  for (const value of found) {
    if (!value) {
      continue;
    }
    const time = new Date(value).getTime();
    if (!Number.isNaN(time) && (!newest || time > newest.time)) {
      newest = { value, time };
    }
  }
  if (newest) {
    return newest.value;
  }

  // No header (e.g. behind the vite dev server): browsers substitute the current time, which
  // would read as a misleading "deployed today", so only accept a plausibly older value.
  const fromDocument = document.lastModified;
  if (!fromDocument) {
    return undefined;
  }
  const parsed = new Date(fromDocument);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  const age = new Date().getTime() - parsed.getTime();
  return age > DOCUMENT_LAST_MODIFIED_MIN_AGE_MS ? fromDocument : undefined;
}

function extractCompletedAt(ambrosiaHead: string): string | undefined {
  const metadataIndex = ambrosiaHead.indexOf('"exportMetadata"');
  if (metadataIndex === -1) {
    return undefined;
  }
  // A sliced body is not valid JSON, so read the field directly instead of parsing.
  const match = /"completedAt"\s*:\s*"([^"]+)"/.exec(ambrosiaHead.slice(metadataIndex));
  return match?.[1];
}

/** `exportMetadata.completedAt` from the package's `ambrosia.js`. */
export async function getPackageExportedAt(packagePath: string): Promise<string | undefined> {
  if (!packagePath) {
    return undefined;
  }

  // WEB/MOBILE load ambrosia.js as a <script>, so the global is already in memory.
  const fromWindow = (window.ambrosia as { exportMetadata?: { completedAt?: string } } | undefined)
    ?.exportMetadata?.completedAt;
  if (fromWindow) {
    return fromWindow;
  }

  // EXPRESS never sets that global. `Range` is not CORS-safelisted, so a cross-origin package
  // may reject the preflight - fall back to an unranged request in that case.
  const url = `${packagePath.replace(/\/$/, "")}/ambrosia.js`;
  try {
    let res: Response;
    try {
      res = await fetch(url, { headers: { Range: `bytes=0-${AMBROSIA_HEAD_BYTES - 1}` } });
    } catch {
      res = await fetch(url);
    }
    if (!res.ok) {
      return undefined;
    }
    return extractCompletedAt(await res.text());
  } catch {
    return undefined;
  }
}

/** `Last-Modified` of the package's `ambrosia.js` - guaranteed present in every package. */
export async function getPackageDeployedAt(packagePath: string): Promise<string | undefined> {
  if (!packagePath) {
    return undefined;
  }
  return readLastModified(`${packagePath.replace(/\/$/, "")}/ambrosia.js`);
}

function joinTooltip(parts: (string | undefined | false)[]): string | undefined {
  const lines = parts.filter(Boolean);
  return lines.length ? lines.join("\n") : undefined;
}

async function resolveShellPackageDates(
  packagePath: string,
  exportId?: string,
): Promise<ShellPackageDates> {
  const [shellAt, packageExportedAt, packageDeployedAt] = await Promise.all([
    getShellLastModified(),
    getPackageExportedAt(packagePath),
    getPackageDeployedAt(packagePath),
  ]);

  return {
    shellAt: formatShortDate(shellAt),
    shellTooltip: joinTooltip([shellAt && `dev-shell written ${shellAt}`]),
    // The deployed date is what "when did this package land" means; the export date stands in
    // when the header is unreadable, so a package date still shows cross-origin.
    packageAt: formatShortDate(packageDeployedAt) ?? formatShortDate(packageExportedAt),
    packageTooltip: joinTooltip([
      packageDeployedAt && `package written ${packageDeployedAt}`,
      packageExportedAt && `package exported ${packageExportedAt}`,
      exportId && `exportId ${exportId}`,
    ]),
  };
}

/**
 * Resolves both dates, memoised per package so re-renders cannot refetch.
 * `packagePath` is empty in online cohort/deployment mode - only the shell date resolves then.
 */
export function getShellPackageDates(
  packagePath: string,
  exportId?: string,
): Promise<ShellPackageDates> {
  const cached = cache.get(packagePath);
  if (cached) {
    return cached;
  }
  const pending = resolveShellPackageDates(packagePath, exportId).catch(() => ({}));
  cache.set(packagePath, pending);
  return pending;
}

/** Test-only: drops the memoised results. */
export function clearBuildDatesCache(): void {
  cache.clear();
}
